import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Printer } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Datenschutz = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    document.title = language === 'de' ? 'SourSweet.de/Datenschutz' : 'SourSweet.de/Privacy';
  }, [language]);

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-emerald-800 pt-24 pb-16 px-4 md:px-8 print:!bg-white print:!pt-0 print:!pb-0 print:!px-0 print:!min-h-0" data-testid="datenschutz-page">
      <div className="max-w-4xl mx-auto">
        {/* Content Card */}
        <div className="bg-lime-50 rounded-2xl shadow-xl p-8 md:p-12 print:!shadow-none print:!p-4 print:!rounded-none print:!bg-white">
          {/* Header */}
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-emerald-800 mb-8 text-center print:text-black">
            {language === 'de' ? 'Datenschutzerklärung' : 'Privacy Policy'}
          </h1>

          {/* Content */}
          <div className="prose prose-emerald max-w-none text-emerald-800 print:text-black">
            
            {language === 'de' ? (
              <>
                {/* German Content */}
                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">1. Verantwortlicher</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Verantwortlich für die Datenverarbeitung auf dieser Website ist:
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    SourSweet<br />
                    Inhaber: Cem Koptas<br />
                    Bärensteinstraße 2<br />
                    12685 Berlin<br />
                    Deutschland
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mt-4">
                    E-Mail: <a href="mailto:info@soursweet.de" className="text-lime-600 hover:underline">info@soursweet.de</a><br />
                    Telefon: +49 162 2755781
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">2. Allgemeine Hinweise zur Datenverarbeitung</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Wir verarbeiten personenbezogene Daten ausschließlich im Rahmen der gesetzlichen Bestimmungen der Datenschutz-Grundverordnung (DSGVO) sowie des Bundesdatenschutzgesetzes (BDSG).
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Personenbezogene Daten werden nur verarbeitet, soweit dies zur Bereitstellung einer funktionsfähigen Website sowie unserer Inhalte und Dienstleistungen erforderlich ist.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">3. Hosting</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Unsere Website wird gehostet von:
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Hostinger International Ltd.<br />
                    61 Lordou Vironos Street<br />
                    6023 Larnaca<br />
                    Zypern
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Beim Besuch unserer Website erfasst der Hosting-Anbieter automatisch sogenannte Server-Logfiles. Diese beinhalten insbesondere:
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>IP-Adresse</li>
                    <li>Datum und Uhrzeit des Zugriffs</li>
                    <li>Browsertyp und Browserversion</li>
                    <li>Verwendetes Betriebssystem</li>
                    <li>Referrer-URL</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Diese Daten dienen der technischen Bereitstellung, Stabilität und Sicherheit der Website.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Mit dem Hosting-Anbieter wurde ein Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO abgeschlossen.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer sicheren und stabilen Website).
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">4. Kontaktformular und Kommunikation</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Wenn Sie uns per Kontaktformular, E-Mail oder Telefon kontaktieren, werden Ihre Angaben zur Bearbeitung Ihrer Anfrage gespeichert.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Dies kann insbesondere folgende Daten umfassen:
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>Name</li>
                    <li>E-Mail-Adresse</li>
                    <li>Telefonnummer</li>
                    <li>Veranstaltungsdetails</li>
                    <li>Sonstige freiwillig übermittelte Informationen</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-2">
                    <strong>Rechtsgrundlage:</strong>
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung oder Vertragserfüllung)</li>
                    <li>Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an effizienter Kommunikation)</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Die Daten werden gelöscht, sobald sie für die Erreichung des jeweiligen Zweckes nicht mehr erforderlich sind und keine gesetzlichen Aufbewahrungspflichten bestehen.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">5. Google reCAPTCHA</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Diese Website nutzt Google reCAPTCHA, bereitgestellt von:
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Google Ireland Limited<br />
                    Gordon House<br />
                    Barrow Street<br />
                    Dublin 4<br />
                    Irland
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    reCAPTCHA dient dazu zu überprüfen, ob Eingaben auf unserer Website durch eine natürliche Person oder missbräuchlich durch automatisierte Verarbeitung erfolgen.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Hierbei können folgende Daten verarbeitet werden:
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>IP-Adresse</li>
                    <li>Mausbewegungen</li>
                    <li>Verweildauer</li>
                    <li>Browserinformationen</li>
                    <li>Geräteinformationen</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt im Schutz unserer Website vor Spam und Missbrauch.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Es kann nicht ausgeschlossen werden, dass Daten in die USA übertragen werden. Google ist nach dem EU-U.S. Data Privacy Framework zertifiziert.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Weitere Informationen: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-lime-600 hover:underline">https://policies.google.com/privacy</a>
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">6. Google Fonts</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Diese Website nutzt Google Fonts zur einheitlichen Darstellung von Schriftarten. Anbieter ist:
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Google Ireland Limited<br />
                    Gordon House<br />
                    Barrow Street<br />
                    Dublin 4<br />
                    Irland
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Beim Aufruf einer Seite lädt Ihr Browser die benötigten Web Fonts in ihren Browsercache. Dabei kann Ihre IP-Adresse an Google übermittelt werden.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer ansprechenden und einheitlichen Darstellung der Website).
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">7. Speicherdauer</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Personenbezogene Daten werden nur so lange gespeichert, wie dies zur Erfüllung des jeweiligen Zwecks erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">8. Ihre Rechte</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Sie haben gemäß DSGVO folgende Rechte:
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>Recht auf Auskunft (Art. 15 DSGVO)</li>
                    <li>Recht auf Berichtigung (Art. 16 DSGVO)</li>
                    <li>Recht auf Löschung (Art. 17 DSGVO)</li>
                    <li>Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                    <li>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
                    <li>Widerspruchsrecht (Art. 21 DSGVO)</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Sie haben zudem das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren.
                  </p>
                </section>
              </>
            ) : (
              <>
                {/* English Content */}
                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">1. Controller</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    The controller responsible for data processing on this website is:
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    SourSweet<br />
                    Owner: Cem Koptas<br />
                    Bärensteinstraße 2<br />
                    12685 Berlin<br />
                    Germany
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mt-4">
                    Email: <a href="mailto:info@soursweet.de" className="text-lime-600 hover:underline">info@soursweet.de</a><br />
                    Phone: +49 162 2755781
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">2. General Information on Data Processing</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    We process personal data exclusively in accordance with the General Data Protection Regulation (GDPR) and applicable German data protection laws.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Personal data is processed only to the extent necessary to provide a functional website and our services.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">3. Hosting</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Our website is hosted by:
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Hostinger International Ltd.<br />
                    61 Lordou Vironos Street<br />
                    6023 Larnaca<br />
                    Cyprus
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    When visiting our website, the hosting provider automatically collects so-called server log files. These include in particular:
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>IP address</li>
                    <li>Date and time of access</li>
                    <li>Browser type and version</li>
                    <li>Operating system used</li>
                    <li>Referrer URL</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    This data ensures the technical functionality, stability, and security of the website.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    A Data Processing Agreement (DPA) pursuant to Art. 28 GDPR has been concluded with the hosting provider.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    The legal basis is Art. 6(1)(f) GDPR (legitimate interest in operating a secure and stable website).
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">4. Contact Form and Communication</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    If you contact us via contact form, email, or telephone, the information you provide will be stored for the purpose of processing your request.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    This may include in particular:
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>Name</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Event details</li>
                    <li>Any other voluntarily provided information</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-2">
                    <strong>Legal basis:</strong>
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>Art. 6(1)(b) GDPR (pre-contractual measures or contract performance)</li>
                    <li>Art. 6(1)(f) GDPR (legitimate interest in efficient communication)</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Data will be deleted once it is no longer required for the purpose collected and no statutory retention obligations apply.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">5. Google reCAPTCHA</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    This website uses Google reCAPTCHA provided by:
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Google Ireland Limited<br />
                    Gordon House<br />
                    Barrow Street<br />
                    Dublin 4<br />
                    Ireland
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    reCAPTCHA is used to determine whether data entered on this website is provided by a human or by automated processing.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    The following data may be processed:
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>IP address</li>
                    <li>Mouse movements</li>
                    <li>Time spent on the website</li>
                    <li>Browser information</li>
                    <li>Device information</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Processing is based on Art. 6(1)(f) GDPR (legitimate interest in preventing spam and misuse).
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Data may be transferred to the United States. Google is certified under the EU-U.S. Data Privacy Framework.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Further information: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-lime-600 hover:underline">https://policies.google.com/privacy</a>
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">6. Google Fonts</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    This website uses Google Fonts for uniform font presentation. Provider is:
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Google Ireland Limited<br />
                    Gordon House<br />
                    Barrow Street<br />
                    Dublin 4<br />
                    Ireland
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    When accessing a page, your browser loads the required web fonts. Your IP address may be transmitted to Google.
                  </p>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Legal basis: Art. 6(1)(f) GDPR (legitimate interest in an appealing and consistent website presentation).
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">7. Data Retention</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    Personal data is stored only as long as necessary for the respective purpose or as required by statutory retention obligations.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">8. Your Rights</h2>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black mb-4">
                    Under the GDPR, you have the following rights:
                  </p>
                  <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black mb-4">
                    <li>Right of access (Art. 15 GDPR)</li>
                    <li>Right to rectification (Art. 16 GDPR)</li>
                    <li>Right to erasure (Art. 17 GDPR)</li>
                    <li>Right to restriction of processing (Art. 18 GDPR)</li>
                    <li>Right to data portability (Art. 20 GDPR)</li>
                    <li>Right to object (Art. 21 GDPR)</li>
                  </ul>
                  <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                    You also have the right to lodge a complaint with a supervisory authority.
                  </p>
                </section>
              </>
            )}

            {/* Last Updated */}
            <p className="text-emerald-600 font-semibold text-sm mt-8 print:text-gray-600">
              {language === 'de' ? 'Zuletzt aktualisiert: Dezember 2024' : 'Last updated: December 2024'}
            </p>
          </div>

          {/* Buttons - Hidden on Print */}
          <div className="flex flex-col sm:flex-row gap-4 mt-10 print:hidden">
            <button
              onClick={handleClose}
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-700 text-white hover:bg-emerald-600 hover:shadow-xl hover:shadow-emerald-400/50 transition-all duration-300 rounded-lg px-6 py-3 font-bold"
              data-testid="close-button"
            >
              <X size={20} />
              {language === 'de' ? 'Schließen' : 'Close'}
            </button>
            <button
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-2 bg-lime-200 text-emerald-800 hover:bg-lime-300 hover:shadow-xl hover:shadow-lime-300/50 transition-all duration-300 rounded-lg px-6 py-3 font-bold"
              data-testid="print-button"
            >
              <Printer size={20} />
              {language === 'de' ? 'Drucken' : 'Print'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
